<!-- Modern Project Management Dashboard -->
<div class="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
  
  <!-- Header Section -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <!-- Title and Description -->
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          {{ "PROJECT_MANAGEMENT" | translate }}
        </h1>
        <p class="text-gray-600">
          Manage and monitor all your construction projects in one place
        </p>
      </div>
      
      <!-- Quick Stats -->
      <div class="flex flex-wrap gap-4">
        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-600">Total Projects</p>
              <p class="text-lg font-semibold text-gray-900">{{ pagination.totalCount || dataSource.data.length }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Search and Filter Section -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
    <div class="flex flex-col lg:flex-row gap-4">
      
      <!-- Search Input -->
      <div class="flex-1">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search projects by name, manager, or stage..."
            class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
            (input)="applyFilter($event)"
          />
        </div>
      </div>

      <!-- Status Filter -->
      <div class="lg:w-64">
        <select
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
          (change)="filterByStatus($event)"
          [value]="selectedStage"
        >
          <option value="">{{ "ALL_STAGES" | translate }}</option>
          <option *ngFor="let stage of stages" [value]="stage.name">
            {{ stage.name | translate }}
          </option>
        </select>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-3">
        <button
          [routerLink]="'/home/add-project'"
          class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          {{ "ADD_PROJECT" | translate }}
        </button>
        
        <button
          [routerLink]="['/home/add-excel-import','Project']"
          class="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
          </svg>
          {{ "IMPORT_PROJECT" | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- Projects Grid/Table -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
    
    <!-- Table Header -->
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">Projects Overview</h2>
      <p class="text-sm text-gray-600 mt-1">Manage all your construction projects</p>
    </div>

    <!-- Projects Table -->
    <div class="overflow-x-auto">
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        
        <!-- Serial Number Column -->
        <ng-container matColumnDef="srNo">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            #
          </th>
          <td mat-cell *matCellDef="let element; let i = index" class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {{ ((pagination.currentPage || 1) - 1) * (pagination.limit || 10) + i + 1 }}
          </td>
        </ng-container>

        <!-- Project Name Column -->
        <ng-container matColumnDef="projectName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "PROJECT_NAME" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
              </div>
              <div>
                <div class="text-sm font-medium text-gray-900">{{ element.project_name }}</div>
                <!-- <div class="text-sm text-gray-500">Project ID: {{ element.id }}</div> -->
              </div>
            </div>
          </td>
        </ng-container>

        <!-- Project Manager Column -->
        <ng-container matColumnDef="projectManager">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "PROJECT_MANAGER" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <div class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="text-sm font-medium text-gray-900">{{ element?.company_name || 'Unassigned' }}</div>
            </div>
          </td>
        </ng-container>

        <!-- Assigned To Column -->
        <ng-container matColumnDef="assignedTo">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "ASSIGNED_TO" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <div class="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div class="text-sm font-medium text-gray-900">{{ element?.assigned_staff_name  || 'Not Assigned'  | titlecase}}  </div>
            </div>
          </td>
        </ng-container>

        <!-- Start Date Column -->
        <ng-container matColumnDef="startDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "START_DATE_" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div class="flex items-center">
              <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              {{ element.timeline_start | date:'MMM dd, yyyy' }}
            </div>
          </td>
        </ng-container>

        <!-- End Date Column -->
        <ng-container matColumnDef="endDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "END_DATE_" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <div class="flex items-center">
              <svg class="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              {{ element.timeline_end | date:'MMM dd, yyyy' }}
            </div>
          </td>
        </ng-container>

        <!-- Stage Column -->
        <ng-container matColumnDef="stage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "STAGE" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <span 
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
              [ngClass]="{
                'bg-blue-100 text-blue-800': element.stage === 'Pre-Planning' || element.stage === 'Planning',
                'bg-yellow-100 text-yellow-800': element.stage === 'Survey' || element.stage === 'Design Revisions & Scope Adjustments',
                'bg-green-100 text-green-800': element.stage === 'Execution',
                'bg-purple-100 text-purple-800': element.stage === 'Closure',
                'bg-gray-100 text-gray-800': !element.stage
              }"
            >
              <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="3"/>
              </svg>
              {{ element.stage || 'Not Set' }}
            </span>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "ACTIONS" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <div class="flex items-center space-x-2">

              <!-- View Project Button (mirrors dashboard's "View Details") -->
              <button
                [routerLink]="['/home/view-project', element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-sky-600 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors duration-200"
                matTooltip="View Project"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>

              <!-- View Survey Details (Survey stage only) -->
              <button
                *ngIf="element.stage === 'Survey'"
                [routerLink]="['/home/survey-list']"
                [queryParams]="{ projectId: element.id, projectName: element.project_name }"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-teal-600 hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors duration-200"
                matTooltip="View Survey Details"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </button>

              <!-- Edit Button -->
              <button
                *ngIf="element.access_mode !== 'awarded'"
                [routerLink]="['/home/edit-project', element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                matTooltip="Edit Project"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>

              <!-- Stage Management Button -->
              <button
                *ngIf="element.access_mode !== 'awarded'"
                (click)="stageProject(element)"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-green-600 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                matTooltip="Manage Stage"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                </svg>
              </button>

              <!-- Documents Button -->
              <button
                [routerLink]="['/home/document-list', element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-200"
                matTooltip="Document List"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </button>

               <!-- GIS Editor Button - Road domain only -->
              <button
                *ngIf="(element.domain || '').toLowerCase() === 'road'"
                [routerLink]="['/home/gis-project', element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-purple-600 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors duration-200"
                matTooltip="GIS Editor"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
              

              <!-- Worklog Button -->
              <button
                [routerLink]="['/home/add-work-project', element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
                matTooltip="View Worklog"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </button>

              <!-- More Actions Dropdown -->
              <div class="relative inline-block text-left">
                <button
                  class="inline-flex items-center p-2 border border-transparent rounded-lg text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                  matTooltip="More Actions"
                  matTooltipPosition="above"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
                  </svg>
                </button>
              </div>

            </div>
          </td>
        </ng-container>

        <!-- Table Header and Rows -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns" class="hover:bg-gray-50 transition-colors duration-150"></tr>
      </table>
    </div>

    <!-- Empty State -->
    <div *ngIf="dataSource.data.length === 0" class="text-center py-12">
      <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
        </svg>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
      <p class="text-gray-500 mb-6">Get started by creating your first project.</p>
      <button
        [routerLink]="'/home/add-project'"
        class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Create First Project
      </button>
    </div>

    <!-- Pagination -->
    <div class="px-6 py-4 border-t border-gray-200 bg-gray-50">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <!-- Pagination Info -->
        <div class="text-sm text-gray-600">
          <span *ngIf="pagination.totalCount > 0">
            Showing {{ ((pagination.currentPage || 1) - 1) * (pagination.limit || 10) + 1 }} to 
            {{ Math.min((pagination.currentPage || 1) * (pagination.limit || 10), pagination.totalCount || 0) }} 
            of {{ pagination.totalCount || 0 }} projects
          </span>
          <span *ngIf="pagination.totalCount === 0">No projects found</span>
        </div>
        
        <!-- Mat Paginator -->
        <mat-paginator 
          [length]="pagination.totalCount" 
          [pageSize]="pagination.limit" 
          [pageIndex]="pagination.currentPage - 1"
          [pageSizeOptions]="[5, 10, 25, 50]"
          showFirstLastButtons
          class="bg-transparent"
        >
        </mat-paginator>
      </div>
    </div>
  </div>
</div>
